// Iter 83.3 / Phase E4 Sprint 3 — FNF Freeze Certificate print route.
// Renders the D28 Freeze Certificate in print-friendly layout with
// SHA-256 hash banner and reproducibility footer.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FnfFreezeCertificatePrint() {
    const { fnfId } = useParams();
    const [cert, setCert] = useState(null);
    const [fnf, setFnf] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [c, f] = await Promise.all([
                    api.get(`/fnf/${fnfId}/certificate`),
                    api.get(`/fnf/${fnfId}`),
                ]);
                setCert(c.data);
                setFnf(f.data);
            } catch (err) {
                setError(formatApiError(err, "Failed to load certificate"));
                toast.error(formatApiError(err, "Load failed"));
            }
        })();
    }, [fnfId]);

    if (error) return <div className="p-12 text-center text-rose-600">{error}</div>;
    if (!cert || !fnf) return <div className="p-12 text-center text-slate-400">Loading…</div>;

    return (
        <div className="bg-white min-h-screen p-12 font-serif" data-testid="fnf-freeze-cert-print">
            <style>{`@media print { @page { size: A4; margin: 12mm; } body { -webkit-print-color-adjust: exact; } }`}</style>
            <header className="border-b-4 border-slate-900 pb-4 mb-6">
                <div className="text-xs uppercase tracking-widest text-slate-600">REEDOS HR & Payroll</div>
                <h1 className="text-3xl font-bold mt-1">Full & Final Settlement — Freeze Certificate</h1>
                <div className="text-sm text-slate-600 mt-1">D28 · Tamper-evident · SHA-256 sealed at approval</div>
            </header>

            <section className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm mb-6">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">FNF ID</div>
                    <div className="font-mono">{cert.payload.fnf_id}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Certificate ID</div>
                    <div className="font-mono">{cert.id}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Employee</div>
                    <div>{cert.payload.employee_name} ({cert.payload.employee_id})</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Exit Type · LWD</div>
                    <div>{cert.payload.exit_type} · {cert.payload.lwd}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Exit Event</div>
                    <div className="font-mono">{cert.payload.exit_event_id}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Approved At</div>
                    <div>{cert.payload.approved_at}</div>
                </div>
            </section>

            <section className="border-t border-slate-300 pt-4 mb-6">
                <h2 className="text-lg font-bold mb-2">Settlement Summary</h2>
                <table className="w-full text-sm">
                    <tbody>
                        <tr className="border-b border-slate-100">
                            <td className="py-1 text-slate-700">Earnings Subtotal</td>
                            <td className="py-1 text-right font-mono">{fmtMoney(cert.payload.earnings_subtotal)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                            <td className="py-1 pl-4 text-slate-600 text-xs">— Gratuity</td>
                            <td className="py-1 text-right font-mono text-xs">{fmtMoney(cert.payload.gratuity_amount)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                            <td className="py-1 pl-4 text-slate-600 text-xs">— Leave Encashment</td>
                            <td className="py-1 text-right font-mono text-xs">{fmtMoney(cert.payload.leave_encashment_amount)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                            <td className="py-1 text-slate-700">Deductions Subtotal</td>
                            <td className="py-1 text-right font-mono">{fmtMoney(cert.payload.deductions_subtotal)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                            <td className="py-1 pl-4 text-slate-600 text-xs">— Loan Recovery (Sprint 1 / D16)</td>
                            <td className="py-1 text-right font-mono text-xs">{fmtMoney(cert.payload.loan_recovery_amount)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                            <td className="py-1 pl-4 text-slate-600 text-xs">— Asset Recovery (Sprint 2 / D18 + D26)</td>
                            <td className="py-1 text-right font-mono text-xs">{fmtMoney(cert.payload.asset_recovery_amount)}</td>
                        </tr>
                        <tr className="border-t-2 border-slate-900">
                            <td className="py-2 font-bold">Net FNF</td>
                            <td className={`py-2 text-right font-mono font-bold text-lg ${cert.payload.is_negative ? "text-rose-700" : "text-emerald-700"}`}>
                                {fmtMoney(cert.payload.net_settlement_amount)}
                                {cert.payload.is_negative && (
                                    <span className="block text-xs text-rose-600 font-normal">D23 Negative · Shortfall {fmtMoney(cert.payload.shortfall_amount)}</span>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section className="border-t border-slate-300 pt-4 mb-6">
                <h2 className="text-lg font-bold mb-2">Approval Chain (D24 4-eye)</h2>
                {(cert.payload.approval_chain || []).map((a, i) => (
                    <div key={i} className="text-sm">
                        <span className="font-semibold">{a.approver_name || a.approver_email}</span>
                        {a.approver_email && <span className="text-slate-500 text-xs"> ({a.approver_email})</span>}
                        <span className="text-slate-500 text-xs ml-2">at {a.approved_at}</span>
                        {a.note && <div className="italic text-xs text-slate-600">"{a.note}"</div>}
                    </div>
                ))}
            </section>

            <footer className="border-t-2 border-slate-900 pt-3 text-xs">
                <div className="font-semibold uppercase tracking-widest text-slate-700">Cryptographic Seal (D28)</div>
                <div className="font-mono mt-1 break-all bg-slate-100 p-2 rounded">{cert.hash}</div>
                <div className="text-slate-500 mt-1">
                    Algorithm: SHA-256 ·{" "}
                    {cert.hash_verified ? (
                        <span className="text-emerald-700 font-semibold">Hash re-verified at view time ✓</span>
                    ) : (
                        <span className="text-rose-700 font-semibold">Hash MISMATCH — possible tampering!</span>
                    )}
                </div>
                <div className="text-slate-400 mt-1">
                    Sealed at approval. Reproducible via deterministic re-hash of certificate.payload (sorted keys, separators-canonical JSON).
                </div>
            </footer>
        </div>
    );
}
